// src/features/merchantConfig/components/states/ErrorState.jsx
//
// Inline error card with a "Try again" CTA that refetches just the currently
// selected category. Driven off `error_code` (per UI prompt §6) — the UI does
// not branch on HTTP status.

import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

/**
 * @param {{ error: Object | null, onRetry?: () => void, isRetrying?: boolean }} props
 */
export default function ErrorState({ error, onRetry, isRetrying }) {
  const errorCode = error?.error_code || "UNKNOWN_ERROR";
  const errorMessage =
    error?.error_message || "We couldn't load this configuration. Please try again.";

  return (
    <div
      role="alert"
      className="rounded-xl border border-red-200 bg-red-50 px-5 py-5"
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600">
          <AlertTriangle className="h-5 w-5" aria-hidden="true" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-sm font-semibold text-red-900">
            Couldn't load this configuration
          </h2>
          <p className="mt-1 text-sm text-red-800">{errorMessage}</p>
          <p className="mt-2 text-xs font-mono text-red-700/80">
            Error code: <span className="font-semibold">{errorCode}</span>
          </p>

          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              disabled={isRetrying}
              className="mt-4 inline-flex items-center gap-2 rounded-lg border border-red-300 bg-white px-3.5 py-2 text-sm font-medium text-red-800 shadow-sm hover:bg-red-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <RefreshCw
                className={`h-4 w-4 ${isRetrying ? "animate-spin" : ""}`}
                aria-hidden="true"
              />
              {isRetrying ? "Retrying…" : "Try again"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
